import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useConnectionsStore } from "@/store/useConnectionsStore";
import { channelsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  Linkedin, ShieldCheck, Loader2, CheckCircle2, ChevronRight, Globe, Lock, Sparkles,
} from "lucide-react";

// Phase 8a₁ — LinkedIn pairing flow with multi-account pooling
// 4 mock steps:
//   1) Enter handle + region
//   2) Spinning up isolated browser ("Provisioning headless session on OpenClaw…")
//   3) MFA / safety check
//   4) Done — added to pool
// All state lives inside the inner flow component, which remounts each open.

const SUGGESTED_HANDLES = [
  { name: "Priya Shah",   handle: "linkedin.com/in/priya-shah-revops" },
  { name: "Devraj Iyer",  handle: "linkedin.com/in/devraj-iyer" },
  { name: "Karan Verma",  handle: "linkedin.com/in/karan-verma-growth" },
];

const REGIONS = ["US-east residential", "EU-west residential", "APAC-south residential"];

function stepCircleClass(step, stepNum) {
  if (step > stepNum) return "bg-emerald-500 text-white ring-emerald-500";
  if (step === stepNum) return "bg-indigo-600 text-white ring-indigo-600";
  return "bg-white text-slate-500 ring-slate-300";
}

function LinkedInPairingFlow({ onClose, connectionId, onConfigured }) {
  const addLinkedInAccount = useConnectionsStore((s) => s.addLinkedInAccount);
  const pool = useConnectionsStore((s) => s.channels.linkedin.pool);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [region, setRegion] = useState(REGIONS[0]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Step 2 → 3 after fake provisioning
  useEffect(() => {
    if (step !== 2) return;
    const t = setTimeout(() => setStep(3), 2200);
    return () => clearTimeout(t);
  }, [step]);

  // Step 3 → 4 after MFA pass — persist to backend when connectionId provided
  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    const finalize = async () => {
      setSaving(true);
      setSaveError(null);
      try {
        if (connectionId) {
          await channelsApi.configureLinkedIn(connectionId, {
            profile_url: handle.trim(),
            display_name: name.trim(),
          });
          await onConfigured?.();
        } else {
          const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "??";
          addLinkedInAccount({
            id: `li-${Date.now()}`,
            name: name || "New account",
            handle: handle || "linkedin.com/in/—",
            avatar: initials,
            pairedAt: "just now",
            safety: 100,
            dailyUsed: 0,
            dailyLimit: 40,
            primary: false,
          });
        }
        if (!cancelled) setStep(4);
      } catch (e) {
        if (!cancelled) {
          setSaveError(e?.message || "Couldn't save LinkedIn configuration");
          toast.error(e?.message || "Couldn't save LinkedIn configuration");
          setStep(1);
        }
      } finally {
        if (!cancelled) setSaving(false);
      }
    };
    const t = setTimeout(finalize, connectionId ? 1200 : 2400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [step, name, handle, addLinkedInAccount, connectionId, onConfigured]);

  const canProceed = name.trim().length > 1 && handle.trim().length > 4;

  return (
    <>
      {/* Stepper */}
      <div className="flex items-center gap-2 -mt-1 mb-1 flex-wrap">
        {[{ n: 1, label: "Account" }, { n: 2, label: "Provision" }, { n: 3, label: "Safety check" }, { n: 4, label: "Added to pool" }].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={cn(
              "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold ring-1 transition-colors",
              stepCircleClass(step, s.n),
            )}>
              {step > s.n ? <CheckCircle2 size={11} /> : s.n}
            </div>
            <span className={cn("text-[11px] font-medium", step >= s.n ? "text-slate-800" : "text-slate-400")}>{s.label}</span>
            {i < 3 && <ChevronRight size={11} className="text-slate-300" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1.5">
            <label htmlFor="linkedin-name-input" className="text-[11px] font-semibold text-slate-700">Display name</label>
            <Input
              id="linkedin-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya Shah"
              className="h-9 text-[12.5px]"
              data-testid="linkedin-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="linkedin-handle-input" className="text-[11px] font-semibold text-slate-700">LinkedIn profile URL</label>
            <Input
              id="linkedin-handle-input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="linkedin.com/in/your-handle"
              className="h-9 text-[12.5px] font-mono"
              data-testid="linkedin-handle-input"
            />
            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
              <span className="text-[10px] text-slate-500">Suggest from team:</span>
              {SUGGESTED_HANDLES.map((s) => (
                <button
                  key={s.handle}
                  type="button"
                  onClick={() => { setName(s.name); setHandle(s.handle); }}
                  className="text-[10px] font-medium text-indigo-600 hover:text-indigo-700 ring-1 ring-indigo-200 rounded-full px-2 py-0.5 hover:bg-indigo-50 transition-colors"
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <fieldset>
            <legend className="text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1">
              <Globe size={11} className="text-slate-500" /> Residential exit region
            </legend>
            <div className="flex items-center gap-1.5 flex-wrap">
              {REGIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRegion(r)}
                  className={cn(
                    "text-[11px] font-medium rounded-full px-2.5 py-1 ring-1 transition-colors",
                    region === r ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-600 ring-slate-200 hover:border-slate-300",
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="text-[10.5px] text-slate-500">Maya routes this account&apos;s traffic through a residential pool to keep your behavior natural.</div>
            </fieldset>
          </div>
          <div className="rounded-md bg-indigo-50 ring-1 ring-indigo-200 px-3 py-2 text-[11px] text-indigo-800 inline-flex items-start gap-1.5">
            <Lock size={11} className="mt-0.5 shrink-0" />
            <span>
              <span className="font-semibold">Stealth-safe:</span> isolated browser per account, human-paced send schedule, automatic backoff on captcha.
              {pool.length > 0 && (
                <span className="ml-1">You already have <span className="font-semibold">{pool.length}</span> account{pool.length === 1 ? "" : "s"} in your pool.</span>
              )}
            </span>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onClose(false)} data-testid="linkedin-cancel">Cancel</Button>
            <Button size="sm" disabled={!canProceed} onClick={() => setStep(2)} data-testid="linkedin-proceed">
              Provision browser →
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="py-8 text-center space-y-3">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
            <Loader2 size={22} className="text-indigo-700 animate-spin" />
          </div>
          <div className="text-[13.5px] font-semibold text-slate-900">Spinning up isolated browser…</div>
          <div className="text-[11.5px] text-slate-500 max-w-sm mx-auto">
            OpenClaw is allocating a residential exit on <span className="font-medium text-slate-700">{region}</span> and warming
            cookies for <span className="font-mono text-slate-700">{handle || "your profile"}</span>.
          </div>
          <div className="mx-auto max-w-xs rounded-md bg-slate-900 text-slate-100 font-mono text-[10.5px] px-3 py-2 text-left leading-relaxed">
            <div className="text-emerald-400">$ openclaw session.create</div>
            <div className="opacity-70">  ↳ exit: {region.toLowerCase().replace(/\s/g, "-")}</div>
            <div className="opacity-70">  ↳ persona: warmed-2025-q4</div>
            <div className="opacity-70">  ↳ fingerprint: spoofed ✓</div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="py-8 text-center space-y-3">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            {saving ? <Loader2 size={22} className="text-amber-700 animate-spin" /> : <ShieldCheck size={22} className="text-amber-700" />}
          </div>
          <div className="text-[13.5px] font-semibold text-slate-900">
            {saving ? "Saving configuration…" : "Running safety check…"}
          </div>
          <div className="text-[11.5px] text-slate-500 max-w-sm mx-auto">
            Verifying account health, daily caps, and pacing model. Maya won&apos;t send anything from this account
            for the first 24h — she observes only.
          </div>
          <div className="flex justify-center gap-1.5 flex-wrap pt-1">
            {["MFA verified", "No captcha events", "Account age > 90d", "Posture: warm"].map((b) => (
              <span key={b} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                <CheckCircle2 size={9} /> {b}
              </span>
            ))}
          </div>
          {saveError && (
            <div className="text-[11px] text-rose-600">{saveError}</div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="py-6 space-y-4" data-testid="linkedin-pair-success">
          <div className="text-center space-y-2">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 animate-in zoom-in-50 duration-300">
              <CheckCircle2 size={26} className="text-emerald-600" />
            </div>
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Added to LinkedIn pool</div>
              <div className="text-[12px] text-slate-600 mt-0.5">{name} · {handle}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2 inline-flex items-center gap-1">
              <Sparkles size={10} className="text-indigo-500" /> Account pool ({pool.length})
            </div>
            <div className="space-y-1.5">
              {pool.map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-[11.5px]">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-white text-[9px] font-semibold">{a.avatar}</span>
                  <span className="font-medium text-slate-800">{a.name}</span>
                  {a.primary && <span className="inline-block rounded-full bg-indigo-100 text-indigo-700 px-1.5 py-0 text-[9px] font-semibold">primary</span>}
                  <span className="ml-auto font-mono text-[10px] text-slate-500">{a.handle}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2 text-[11px] text-emerald-800 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Maya will rotate this account into her send mix after a 24h warm-up window.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onClose(true)} data-testid="linkedin-pair-close">
              Close
            </Button>
            <Button size="sm" onClick={() => onClose(true)} data-testid="linkedin-pair-done">Done</Button>
          </div>
        </div>
      )}
    </>
  );
}

export function LinkedInPairingDialog({ open, onOpenChange, connectionId, onConfigured }) {
  const cancelPairing = useConnectionsStore((s) => s.cancelPairing);
  const poolSize = useConnectionsStore((s) => s.channels.linkedin.pool?.length || 0);

  const handleOpenChange = (next) => {
    if (!next && !connectionId && poolSize === 0) cancelPairing("linkedin");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="linkedin-pair-dialog" preventOutsideDismiss>
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-sky-100">
              <Linkedin size={15} className="text-sky-700" />
            </span>{" "}
            Pair LinkedIn account
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Maya runs an isolated headless browser per account · powered by OpenClaw&apos;s session pool.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <LinkedInPairingFlow
            onClose={() => onOpenChange(false)}
            connectionId={connectionId}
            onConfigured={onConfigured}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

LinkedInPairingFlow.propTypes = {
  onClose: PropTypes.func.isRequired,
  connectionId: PropTypes.string,
  onConfigured: PropTypes.func,
};

LinkedInPairingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  connectionId: PropTypes.string,
  onConfigured: PropTypes.func,
};
